import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2 } from '@coursera/cds-core';

import uuid from 'bundles/common/utils/uuid';
import { localPaymentMethodsMapping, paymentMethods } from 'bundles/payments-common/common/constants';
import { OWNERSHIP_POLLING_TIMEOUT } from 'bundles/payments-common/common/constants';
import { RAZORPAY_POLLING_TIMEOUT } from 'bundles/payments-common/utils/razorpayClientUtils';
import PromotionEligibilityTypes from 'bundles/promotions/common/PromotionEligibilityTypes';

import _t from 'i18n!nls/payments';

export const getErrorTitle = (code: string): string | undefined => {
  const ERROR_TITLES: Record<string, string> = {
    'invoice-validation-error': _t('Invoice Generation Failed'),
    'invoice-general-error': _t('Invoice Generation Failed'),
    'stripe-decline-bank-try-again': _t('Payment Failed - Card Declined by Bank'),
    'stripe-decline-do-not-retry': _t('Payment Failed - Card Declined by Bank'),
    'stripe-decline-incorrect-details': _t('Payment Failed - Incorrect Card Details'),
    'stripe-decline-insufficient-funds': _t('Payment Failed - Insufficient Funds'),
    'stripe-decline-temporary-issue': _t('Payment Failed - Temporary Issue'),
  };

  return ERROR_TITLES[code];
};

// See https://razorpay.com/docs/errors/error-reasons/ for an explanation of all the error reason codes
// This is accessed from RazorpayRawErrorResponse `error.reason`
export const getRazorpayErrorMessage = (code: string): string | undefined => {
  const defaultMessage = _t(
    'Sorry, your bank is unable to accept this transaction. Please try again using a different payment method.'
  );

  /* eslint-disable camelcase */
  const messages: Record<string, string> = {
    input_validation_failed: defaultMessage,
    card_network_not_enabled: defaultMessage,
    invalid_currency: defaultMessage,
    recurring_payment_not_enabled: defaultMessage,
    live_mode_not_enabled: defaultMessage,
    invalid_amount: defaultMessage,
    payment_cancelled: _t('Sorry, there was an issue completing your payment. Please try again.'),
    transaction_limit_exceeded: defaultMessage,
    payment_session_expired: _t("We couldn't complete this payment because your session timed out. Please try again."),
    card_not_enrolled: defaultMessage,
    transaction_daily_limit_exceeded: _t(
      "Sorry, this card's maximum transaction limit was exceeded. You can try again in 24 hours, or pay with a different method."
    ),
    insufficient_funds: _t('Please enter a method of payment with sufficient funds.'),
    international_transaction_not_allowed: _t('To enable international transactions, please contact Razorpay.'),
    debit_instrument_blocked: defaultMessage,
    debit_instrument_inactive: defaultMessage,
    incorrect_card_details: _t('Please enter the correct card details to complete this payment.'),
    otp_attempts_exceeded: defaultMessage,
    pin_attempts_exceeded: _t(
      "We couldn't process this payment because there were too many attempts to enter the PIN. Please retry using a different payment method."
    ),
    incorrect_pin: defaultMessage,
    incorrect_cvv: defaultMessage,
    card_expired: defaultMessage,
    payment_timed_out: _t(
      "We couldn't complete this payment because the transaction period timed out. Please try again."
    ),
    authentication_failed: _t('Please enter the correct card details to complete this payment.'),
    card_number_invalid: _t('Please enter a valid credit card number to complete this payment.'),
    card_type_invalid: defaultMessage,
    incorrect_cardholder_name: _t('Please enter the correct cardholder name.'),
    incorrect_card_expiry_date: _t('Please enter the expiration date that matches the one on your card.'),
    transaction_daily_count_exceeded: _t(
      'Sorry, the maximum number of transactions on this card has been exceeded. You can try again in 24 hours, or pay with a different method.'
    ),
    incorrect_otp: _t(
      'It looks like the one-time password you entered is incorrect. Please request a new one-time password and try again.'
    ),
    capture_failed: defaultMessage,
    payment_risk_check_failed: defaultMessage,
    card_declined: _t(
      'The bank that issued this card declined this transaction. Please contact your bank to get more details.'
    ),
    bank_technical_error: _t(
      "We couldn't process this payment because of a technical problem with your bank. Please try another payment method."
    ),
    gateway_technical_error: _t(
      'Sorry, it looks like there was a technical error. You can retry this transaction later or use another payment method.'
    ),
    server_error: _t(
      'Sorry, Razorpay is experiencing a technical error. Please try making this payment later or contact Razorpay.'
    ),
    invalid_vpa: defaultMessage,
    mobile_number_invalid: defaultMessage,
    payment_collect_request_expired: defaultMessage,
    invalid_device: defaultMessage,
    bank_account_invalid: defaultMessage,
    pin_not_set: defaultMessage,
    payment_declined: _t(
      'Sorry, it looks like there was a technical error. Please contact your bank to clear up this problem.'
    ),
    upi_app_technical_error: _t(
      'Sorry, it looks like there was a technical error. Try making this transaction again. If the error persists, try a different form of payment.'
    ),
    payment_pending: _t(
      "This payment is pending authorization. We aren't sure how long this will take. Please try again later."
    ),
    [RAZORPAY_POLLING_TIMEOUT]: _t(
      'We are working on processing your payment. You should receive an email confirmation once we have successfully processed your payment.'
    ),
    vpa_resolution_failed: defaultMessage,
    payment_failed: _t(
      "We couldn't process this payment due to an error at the bank. Please try again with another payment method."
    ),
    bank_not_enabled: _t(
      'It looks like Razorpay has not enabled this bank to complete the transaction. To enable it, contact Razorpay.'
    ),
    amount_less_than_minimum_amount: defaultMessage,
    payment_method_not_enabled: defaultMessage,
    bank_account_validation_failed: _t(
      "We couldn't verify these bank account details. To make this transaction, enter the correct account details."
    ),
    payment_pending_approval: _t(
      'This payment requires approval. Please reach out to the approver in your organization.'
    ),
    user_not_registered_for_netbanking: defaultMessage,
    invalid_user_details: defaultMessage,
    invalid_email: defaultMessage,
    otp_expired: defaultMessage,
    issuer_technical_error: _t(
      'Sorry, it looks like there was a technical error. Try making this transaction again. If the error persists, use a different form of payment.'
    ),
    user_not_eligible: defaultMessage,
    credit_limit_not_approved: defaultMessage,
    credit_limit_expired: defaultMessage,
    credit_limit_inactive: defaultMessage,
    credit_limit_exceeded: defaultMessage,
    emi_greater_than_max_amount: defaultMessage,
    emi_plan_unavailable: defaultMessage,
    invalid_mobile_number: _t(
      "We couldn't find a Wallet account with this phone number. Please enter a registered phone number to complete the transaction."
    ),
  };
  /* eslint-enable camelcase */

  // defaultMessage is not returned as a default here because this method is used as a fall-through in a generic error handler
  // So it should *only* return an error message if the error reason code came from Razorpay
  return messages[code];
};

// This returns an error message based on a human readable code (ex. paypal-error)
export const getErrorMessageForHumanReadableCode = (
  code: string,
  messageProps?: { validationErrors?: string[]; selectedPaymentMethod?: string | null; productPageUrl?: string },
  omitDefaultMessage?: boolean
): string | JSX.Element | undefined => {
  let defaultFieldsError: string | undefined;

  if (!omitDefaultMessage) {
    defaultFieldsError = _t('Please make sure all the fields are filled out correctly.');
  }

  const messages: Record<string, string | JSX.Element | undefined> = {
    'card-fields-error':
      // eslint-disable-next-line no-nested-ternary
      messageProps?.selectedPaymentMethod === paymentMethods.klarna &&
      messageProps?.validationErrors?.includes('Country is not correct.') ? (
        <div>
          <div>
            <b>
              {_t('There was a problem processing your payment', {
                paymentMethodName:
                  localPaymentMethodsMapping[
                    messageProps?.selectedPaymentMethod as keyof typeof localPaymentMethodsMapping
                  ],
              })}
            </b>
          </div>
          <div>
            {_t(
              'Klarna is not supported in your country. Please update your billing information or select another payment method.'
            )}
          </div>
        </div>
      ) : messageProps?.validationErrors?.length ? (
        <div>
          <div>{_t('There was a problem.')}</div>
          <ul className="list">
            {messageProps.validationErrors.map((message) => (
              <li className="spaced" key={uuid()}>
                {message}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        defaultFieldsError
      ),
    'card-invalid-error': _t(
      `Sorry, there was a problem processing your payment. Please check that you typed credit
      card number, CVV, and expiration date correctly.`
    ),
    'card-declined-error': _t(
      `Sorry, there was a problem processing your credit card. Please try again with a
      different credit card.`
    ),
    'paypal-buyer-revoked-payment-authorization-error': _t(
      `Sorry, we no longer have authorization to charge your PayPal account. Please refresh this page
      and try to authorize PayPal again.`
    ),
    'paypal-error': _t(
      `Sorry, PayPal was unable to process your payment. Please try again. You may also pay using a
      credit card.`
    ),
    'paypal-missing-login': _t(
      `Sorry, you must be logged in to PayPal in order to submit. Please log in to PayPal and try again.
      You may also pay using a credit card.`
    ),
    'paypal-unsupported-error': _t(
      `Sorry, PayPal is not supported on your browser.
      Please pay using a credit card or a different browser.`
    ),
    'timeout-error': _t('Sorry, your request timed out. Please try again at a later time.'),
    'invalid-coupon': _t('Sorry, there was an issue with this promotion. Please try enrolling again.'),
    'promotion-redemption-limit-reached': _t('Sorry, the total redemption limit for this promotion has been reached.'),
    'stripe-invalid-currency': _t(
      `Sorry, we cannot complete this purchase as you already have an active subscription in a different currency. 
      Please try again from the same country as your active subscription, or create a new account.`
    ),
    'enterprise-contract-error': (
      <FormattedMessage
        message={_t('Sorry, there was a problem processing your payment. Please {contactLink}.')}
        contactLink={
          <a href="https://courseraenterprise.zendesk.com/hc/en-us" target="_blank" rel="noopener noreferrer">
            {_t('contact Coursera for assistance')}
          </a>
        }
      />
    ),
    'enterprise-contract-refresh-error': (
      <FormattedMessage
        message={_t('Sorry, there was a problem processing your payment. Please {contactLink}.')}
        contactLink={
          <a href="https://learner.coursera.help/hc/requests/new" target="_blank" rel="noopener noreferrer">
            {_t('contact Coursera for assistance')}
          </a>
        }
      />
    ),
    'invalid-contract-billing-country-input': (
      <FormattedMessage
        message={_t(
          'Due to international regulations, we can’t complete transactions with this billing country. Need help? {contactLink} '
        )}
        contactLink={
          <a href="https://www.coursera.com/support" target="_blank" rel="noopener noreferrer">
            {_t('Contact Support')}
          </a>
        }
      />
    ),
    [OWNERSHIP_POLLING_TIMEOUT]: _t(
      'We are working on processing your payment. You should receive an email confirmation once we have successfully processed your payment.'
    ),
    'general-error': (
      <FormattedMessage
        message={_t('Sorry, there was a problem processing your payment. Please {contactLink}.')}
        contactLink={
          <a href="https://learner.coursera.help/hc/requests/new" target="_blank" rel="noopener noreferrer">
            {_t('contact Coursera for assistance')}
          </a>
        }
      />
    ),
    'stripe-error': (
      <FormattedMessage
        message={_t('Sorry, there was a problem processing your payment. Please {contactLink}.')}
        contactLink={
          <a href="https://learner.coursera.help/hc/requests/new" target="_blank" rel="noopener noreferrer">
            {_t('contact Coursera for assistance')}
          </a>
        }
      />
    ),
    'stripe-validation-error': (
      <div>
        <div>
          <b>
            {messageProps?.selectedPaymentMethod &&
            localPaymentMethodsMapping[messageProps?.selectedPaymentMethod as keyof typeof localPaymentMethodsMapping]
              ? _t('There was a problem processing your payment with #{paymentMethodName}', {
                  paymentMethodName:
                    localPaymentMethodsMapping[
                      messageProps?.selectedPaymentMethod as keyof typeof localPaymentMethodsMapping
                    ],
                })
              : _t('There was a problem processing your payment')}
          </b>
        </div>
        {messageProps?.selectedPaymentMethod &&
        [paymentMethods.card, paymentMethods.stripe].includes(messageProps.selectedPaymentMethod) ? (
          <div>{_t(`Enter your card details and try again.`)}</div>
        ) : (
          <div>{_t(`Please try again or try an alternate payment method.`)}</div>
        )}
      </div>
    ),
    'stripe-invalid-request-error': (
      <div>
        <div>
          <b>
            {messageProps?.selectedPaymentMethod &&
            localPaymentMethodsMapping[messageProps?.selectedPaymentMethod as keyof typeof localPaymentMethodsMapping]
              ? _t('There was a problem processing your payment with #{paymentMethodName}', {
                  paymentMethodName:
                    localPaymentMethodsMapping[
                      messageProps?.selectedPaymentMethod as keyof typeof localPaymentMethodsMapping
                    ],
                })
              : _t('There was a problem processing your payment')}
          </b>
        </div>
        <div>{_t(`Your payment method won't be charged. Select a different payment method and try again.`)}</div>
      </div>
    ),
    'stripe-invalid-parameter-error': (
      <div>
        <div>
          <b>
            {messageProps?.selectedPaymentMethod &&
            localPaymentMethodsMapping[messageProps?.selectedPaymentMethod as keyof typeof localPaymentMethodsMapping]
              ? _t('There was a problem processing your payment with #{paymentMethodName}', {
                  paymentMethodName:
                    localPaymentMethodsMapping[
                      messageProps?.selectedPaymentMethod as keyof typeof localPaymentMethodsMapping
                    ],
                })
              : _t('There was a problem processing your payment')}
          </b>
        </div>
        <div>
          {messageProps?.selectedPaymentMethod === paymentMethods.klarna
            ? _t(
                'Klarna is not supported in your country. Please update your billing information or select another payment method.'
              )
            : _t(`Your payment method won't be charged. Select a different payment method and try again.`)}
        </div>
      </div>
    ),
    'stripe-incomplete-intent': (
      <div>
        <div>
          <b>{_t('There was a problem processing your payment')}</b>
        </div>
        <div>{_t(`Your payment method won't be charged. Select a different payment method and try again.`)}</div>
      </div>
    ),
    'checkout-intent-missing-address': (
      <div>
        <div>
          <b>{_t('There was a problem processing your payment')}</b>
        </div>
        <div>{_t('Please update your billing information and try again.')}</div>
      </div>
    ),
    'checkout-intent-expired': (
      <div>
        <div>
          <b>{_t('There was a problem processing your payment')}</b>
        </div>
        <div>{_t(`Please try again or use a different payment method.`)}</div>
      </div>
    ),
    'checkout-intent-reinitialize': (
      <div>
        <div>
          <b>{_t('There was a problem processing your payment')}</b>
        </div>
        <div>{_t(`Please try again or use a different payment method.`)}</div>
      </div>
    ),
    courseraPlusAmexPromoIneligible: _t('Invalid AMEX card information'),
    'expired-cart': messageProps?.productPageUrl ? (
      <FormattedMessage
        message={_t(
          'Sorry, your cart has expired due to inactivity. To start a new checkout, please {productPageUrl}.'
        )}
        productPageUrl={
          <a href={messageProps?.productPageUrl} target="_blank" rel="noopener noreferrer">
            {_t('click here')}
          </a>
        }
      />
    ) : (
      <FormattedMessage
        message={_t('Sorry, there was a problem processing your payment. Please {contactLink}.')}
        contactLink={
          <a href="https://learner.coursera.help/hc/requests/new" target="_blank" rel="noopener noreferrer">
            {_t('contact Coursera for assistance')}
          </a>
        }
      />
    ),
    finAidCartNotValidForCheckout: _t(
      `Financial aid applications take 16 days to review. We'll send you an e-mail update when your application has been processed.`
    ),
    'unverified-email': (
      <div>
        <div>
          <b>{_t('There was a problem verifying your account')}</b>
        </div>
        <div>{_t('Confirm your email address in your inbox, then refresh this page to proceed.')}</div>
      </div>
    ),
    'stripe-decline-bank-try-again': (
      <>
        <Typography2 variant="bodyPrimary" component="p">
          {_t('Your bank declined this transaction. This can happen for security or policy reasons.')}
        </Typography2>
        <Typography2 variant="bodyPrimary" component="p">
          {_t('Please try again or use a different card.')}
        </Typography2>
      </>
    ),
    'stripe-decline-do-not-retry': (
      <>
        <Typography2 variant="bodyPrimary" component="p">
          {_t('Your bank declined this transaction. This can happen for security or policy reasons.')}
        </Typography2>
        <Typography2 variant="bodyPrimary" component="p">
          {_t('Please try a different card.')}
        </Typography2>
      </>
    ),
    'stripe-decline-incorrect-details': (
      <>
        <Typography2 variant="bodyPrimary" component="p">
          {_t('Some of your card information appears to be incorrect.')}
        </Typography2>
        <Typography2 variant="bodyPrimary" component="p">
          {_t('Please double-check your information and try again.')}
        </Typography2>
      </>
    ),
    'stripe-decline-insufficient-funds': (
      <>
        <Typography2 variant="bodyPrimary" component="p">
          {_t("Your card doesn't have enough balance to complete this payment.")}
        </Typography2>
        <Typography2 variant="bodyPrimary" component="p">
          {_t('Please try a different card.')}
        </Typography2>
      </>
    ),
    'stripe-decline-temporary-issue': (
      <>
        <Typography2 variant="bodyPrimary" component="p">
          {_t("We couldn't process the payment due to a temporary issue with your bank or network.")}
        </Typography2>
        <Typography2 variant="bodyPrimary" component="p">
          {_t('Please try again or use a different card.')}
        </Typography2>
      </>
    ),
    'invoice-validation-error': (
      <>
        <Typography2 variant="bodyPrimary" component="p">
          {_t('The Tax ID or Tax Type you provided is incorrect. Please double-check the values.')}
        </Typography2>
      </>
    ),
    'invoice-general-error': (
      <>
        <Typography2 variant="bodyPrimary" component="p">
          {_t(
            "We're sorry, but an unexpected error occurred while processing your request. Please try again in a few moments."
          )}
        </Typography2>
      </>
    ),
  };

  return messages[code] || getRazorpayErrorMessage(code);
};

// This returns an error message based on a numeric payment processor string code (ex. 2038)
// The origins of the messages can be found here
// https://docs.google.com/document/d/1SMyqHG3mgrKeosBnRozDiZqt04Q-n21nCzUL7dwTZyk/edit?ts=5acd9f6f#heading=h.k85xyzan3xmz
export const getErrorMessageForPaymentProcessorCode = (code: string): string | JSX.Element | typeof undefined => {
  const messages: Record<string, string | JSX.Element> = {
    '2004': _t('This credit card has expired. Please try again using a different payment method.'),
    '2005': _t(
      `The credit card number you entered is invalid or is not the correct method of payment. Check your entry to make sure it
      is accurate and try again. If this error persists, please try again using a different payment method.`
    ),
    '2010': _t(
      `The CVV security code you entered is invalid. Check your CVV to make sure it is accurate and try again. If this error
      persists, please try again using a different payment method.`
    ),
    '2015': _t('Your bank declined the transaction. Please try again using a different payment method.'),
    '2019': _t('Your bank declined the transaction. Please try again using a different payment method.'),
    '2038': _t(
      'Sorry, your bank is unable to accept this transaction. Please try again using a different payment method.'
    ),
    '2046': _t('Your bank declined the transaction. Please try again using a different payment method.'),
    '2074': _t(
      'The payment method associated with your PayPal account was declined. Please try again using a different payment method.'
    ),
    '3000': (
      <FormattedMessage
        message={_t(
          'We’ve encountered a payment processing error. Please try again in a few minutes. If this problem persists, please {contactLink} and mention error code 3000.'
        )}
        contactLink={
          <a
            href="https://learner.coursera.help/hc/articles/209819083-Solve-problems-with-payments"
            target="_blank"
            rel="noopener noreferrer"
          >
            {_t('contact Coursera for assistance')}
          </a>
        }
      />
    ),
  };
  return messages[code];
};

export const getPromoErrorMessageFromErrorCode = (code: string): string | undefined => {
  const {
    PROMOTION_INACTIVE,
    TOTAL_REDEMPTION_LIMIT_REACHED,
    USER_REDEMPTION_LIMIT_REACHED,
    PROMOTION_CANNOT_BE_APPLIED,
  } = PromotionEligibilityTypes;

  const messages: Partial<Record<typeof code, string>> = {
    [PROMOTION_INACTIVE]: _t('We’re sorry, but this promotion is no longer available.'),
    [TOTAL_REDEMPTION_LIMIT_REACHED]: _t('We’re sorry, but this promotion is no longer available.'),
    [USER_REDEMPTION_LIMIT_REACHED]: _t('You have already used this promotion code.'),
    [PROMOTION_CANNOT_BE_APPLIED]: _t(
      'We’re sorry, the promotion you tried to apply is currently unavailable due to a technical issue'
    ),
  };
  return messages[code];
};
